import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type { Package, CreatePackageRequest, ListResult } from '@/types'

// ── Packages ─────────────────────────────────────────────────────────────────

export function usePackages() {
  const api = useApiClient()
  return useQuery({
    queryKey: ['packages'],
    queryFn: () => api.get<ListResult<Package>>('/api/v1/packages'),
    // Poll every 3s while any package is still waiting for HawkBit to create the Distribution Set.
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.data?.some(p => !p.hawkbit_distribution_set_id)) return 3000
      return false
    },
  })
}

export function usePackage(packageId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['package', packageId],
    queryFn: () => api.get<Package>(`/api/v1/packages/${packageId}`),
    enabled: !!packageId,
    // poll until hawkbit IDs are populated
    refetchInterval: (query) => {
      const data = query.state.data
      if (data && !data.hawkbit_distribution_set_id) return 3000
      return false
    },
  })
}

export function useCreatePackage() {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreatePackageRequest) =>
      api.post<Package>('/api/v1/packages', req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['packages'] }),
  })
}

export function useDeletePackage() {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (packageId: string) => api.del(`/api/v1/packages/${packageId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['packages'] }),
  })
}

export function useUploadArtifact(packageId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api.postForm(`/api/v1/packages/${packageId}/artifact`, form)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['packages'] })
      qc.invalidateQueries({ queryKey: ['package', packageId] })
    },
  })
}

// ── Rollouts ─────────────────────────────────────────────────────────────────

export interface CreateRolloutRequest {
  fleet_id?: string
  params: {
    package_id: string
    success_threshold?: number
    error_threshold?: number
  }
}

export function useCreateRollout(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateRolloutRequest) =>
      api.post(`/api/v1/tenants/${tenantId}/rollouts`, { ...req, type: 'ota_rollout' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollouts', tenantId] }),
  })
}

export function useRollouts(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['rollouts', tenantId],
    queryFn: () => api.get(`/api/v1/tenants/${tenantId}/rollouts`),
    enabled: !!tenantId,
    // Poll while any rollout is in a non-terminal state.
    refetchInterval: (query) => {
      const data = (query.state.data as any)?.data as any[]
      if (data?.some(op => !['success', 'failed', 'cancelled'].includes(op.status))) return 10000
      return false
    },
  })
}

export interface RolloutProgress {
  id: number
  name: string
  status: string
  totalTargetsCount: number
  finishedPercent: number
  totalTargetsPerStatus?: Record<string, number>
}

export function useRolloutProgress(
  tenantId: string | null,
  rolloutId: string | null,
  active: boolean,
) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['rollout-progress', tenantId, rolloutId],
    queryFn: () =>
      api.get<RolloutProgress>(`/api/v1/tenants/${tenantId}/rollouts/${rolloutId}/progress`),
    enabled: !!tenantId && !!rolloutId && active,
    refetchInterval: active ? 5000 : false,
  })
}

export function useStartRollout(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rolloutId: string) =>
      api.post(`/api/v1/tenants/${tenantId}/rollouts/${rolloutId}/start`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollouts', tenantId] }),
  })
}

export function useCancelRollout(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rolloutId: string) =>
      api.post(`/api/v1/tenants/${tenantId}/operations/${rolloutId}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollouts', tenantId] }),
  })
}

export function useAssignDeviceOTA(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, packageId }: { deviceId: string; packageId: string }) =>
      api.post(`/api/v1/tenants/${tenantId}/devices/${deviceId}/ota-assign`, { package_id: packageId }),
    onSuccess: (_data, { deviceId }) => {
      qc.invalidateQueries({ queryKey: ['device', tenantId, deviceId] })
      qc.invalidateQueries({ queryKey: ['operations', tenantId] })
    },
  })
}
